import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

// ─── TWO CALLERS / TWO API SHAPES ────────────────────────────────────────────
//
// 1) AllDeliveryOrders → GET /api/DeliveryOrders (GetAll projection)
//    data.deliveryOrderItems[]:
//      quantityReceived  ← C# renames item.Quantity to this
//      quantityExpected  ← derived from SO line
//      itemCode          ← enriched (Products.ItemCode)
//      unitName          ← enriched (UnitOfMeasurements.UnitName)
//      unitPrice, lineTotal, productName, relatedLineItemId, priceType
//
// 2) SODeliveryOrdersModal → GET /api/DeliveryOrders/bySo/{id}
//    data.items[]:
//      quantityDelivered ← C# renames i.Quantity to this
//      itemCode          ← enriched
//      unitName          ← enriched (UnitOfMeasurements.UnitName)
//      uomName           ← also present (UnitOfMeasurements.Code — short code)
//      unitPrice, lineTotal, productName
//
// FIX: normalise both into rawItems, use resolveQty() to handle both field names
// ─────────────────────────────────────────────────────────────────────────────

export const DeliveryOrderPrint = React.forwardRef(({ data }, ref) => {
  const barcodeRef = useRef(null);
  const doNumber = data?.deliveryOrderNumber;

  useEffect(() => {
    if (doNumber && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, doNumber, {
          format: "CODE128",
          lineColor: "#000000",
          width: 1.5,
          height: 40,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {
        console.error("Barcode error", e);
      }
    }
  }, [doNumber]);

  if (!data) return null;

  // Normalise items array — handle both GetAll and GetBySo shapes
  const rawItems = data.deliveryOrderItems || data.items || [];

  // Safe quantity resolver — GetAll uses quantityReceived, GetBySo uses quantityDelivered
  const resolveQty = (item) =>
    item.quantityReceived ?? // GetAll
    item.quantityDelivered ?? // GetBySo
    0;

  // UOM label resolver — prefer unitName (full name), fall back to uomName (short code)
  const resolveUom = (item) => item.unitName || item.uomName || "";

  const deliveryDate = data.deliveryDate || data.date;
  const salesOrderNumber = data.salesOrderNumber;
  const quoteNumber = data.quoteNumber;
  const totalAmount = data.totalAmount;
  const vatAmount = data.vatAmount || 0;
  const ewtAmount = data.ewtAmount || 0;

  const totalQty = rawItems.reduce((s, i) => s + resolveQty(i), 0);

  // ── Dynamic document title ──
  // "DISPATCH NOTE" when dispatched or delivered, otherwise "DELIVERY ORDER"
  const status = (data.status || "").toLowerCase();
  const documentTitle =
    status === "dispatched" || status === "delivered"
      ? "DISPATCH NOTE"
      : "PICKING LIST";

  const fmt = (v) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(v || 0);

  return (
    <div
      ref={ref}
      className="p-8 bg-white text-black text-xs font-sans max-w-[800px] mx-auto"
    >
      {/* ── HEADER ── */}
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold uppercase">Ichthus Technology</h1>
        <div className="text-[10px] text-gray-600">
          <p># 3128 Sto. Rosario Street, Barangay</p>
          <p>Mapulang Lupa, Valenzuela, 1440 Metro</p>
        </div>
        <div className="mt-2 flex justify-center">
          <svg ref={barcodeRef}></svg>
        </div>
        <p className="text-[9px] mt-1 tracking-widest">{doNumber}</p>
      </div>

      {/* ── INFO GRID ── */}
      <div className="flex justify-between items-start mb-6 gap-4">
        {/* Ship To — flat fields */}
        <div className="w-1/2">
          <h2 className="font-bold text-sm uppercase mb-1 bg-gray-100 p-1">
            Ship To:
          </h2>
          <div className="pl-1 space-y-0.5">
            <p className="font-bold uppercase text-sm">
              {data.customerName || "WALK-IN CUSTOMER"}
            </p>
            <p className="uppercase w-3/4">
              {data.shippingAddress || "No Address Provided"}
            </p>
          </div>
        </div>

        {/* DO Details */}
        <div className="w-1/2">
          {/* ── Dynamic Title ── */}
          <h2 className="font-bold text-lg mb-2 text-right">{documentTitle}</h2>
          <div className="grid grid-cols-2 gap-y-1 text-xs text-right">
            <span className="font-bold text-gray-600">Delivery Order No.</span>
            <span className="font-semibold">{doNumber}</span>

            {salesOrderNumber && (
              <>
                <span className="font-bold text-gray-600">
                  Sales Order Ref.
                </span>
                <span className="font-semibold">{salesOrderNumber}</span>
              </>
            )}

            {quoteNumber && quoteNumber !== "N/A" && (
              <>
                <span className="font-bold text-gray-600">Quotation Ref.</span>
                <span className="font-semibold">{quoteNumber}</span>
              </>
            )}

            <span className="font-bold text-gray-600">Delivery Date</span>
            <span>
              {deliveryDate ? new Date(deliveryDate).toLocaleDateString() : "—"}
            </span>

            <span className="font-bold text-gray-600">Branch / Location</span>
            <span>{data.locationName || "Main Office"}</span>
          </div>

          <div className="mt-4 bg-gray-100 p-2 border border-gray-200">
            <div className="flex justify-between font-bold">
              <span>Total Items</span>
              <span>{totalQty} units</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABLE HEADER ── */}
      <div className="mt-4 border-b-2 border-black pb-1 mb-1 bg-gray-100 flex font-bold text-xs uppercase p-1">
        <div className="w-6">#</div>
        <div className="w-24">Item Code</div>
        <div className="flex-1">Description</div>
        <div className="w-24 text-center">Qty / UOM</div>
        <div className="w-24 text-right">Unit Price</div>
        <div className="w-24 text-right">Total</div>
      </div>

      {/* ── TABLE BODY ── */}
      <div className="min-h-[300px] font-sans tracking-tight">
        <div className="divide-y divide-gray-100 border-b border-gray-100">
          {rawItems.map((item, index) => {
            const qty = resolveQty(item);
            const price = item.unitPrice ?? 0;
            const lineTotal = item.lineTotal ?? qty * price;
            const uomLabel = resolveUom(item);
            const itemCode = item.itemCode || "—";

            return (
              <div
                key={item.id || index}
                className="flex items-start py-1.5 text-[10px] uppercase font-bold leading-snug"
              >
                <div className="w-6 text-center text-gray-300 pt-0.5">
                  {(index + 1).toString().padStart(2, "0")}
                </div>

                <div className="w-24 text-indigo-500 truncate px-1 pt-0.5 font-mono">
                  {itemCode}
                </div>

                <div className="flex-1 text-gray-800 truncate px-1 pt-0.5">
                  {item.productName}
                </div>

                <div className="w-24 flex justify-center items-start gap-0.5 pt-0.5">
                  <span className="text-gray-900">{qty}</span>
                  {uomLabel && (
                    <span className="text-gray-400 text-[9px] ml-0.5 normal-case">
                      {uomLabel}
                    </span>
                  )}
                </div>

                <div className="w-24 text-right text-gray-500 tabular-nums px-1 pt-0.5">
                  {fmt(price)}
                </div>

                <div className="w-24 text-right text-gray-900 tabular-nums pl-1 pr-2 pt-0.5">
                  {fmt(lineTotal)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── TOTALS ── */}
      <div className="mt-4 border-t-2 border-black pt-2 flex justify-end">
        <div className="w-1/2 space-y-1">
          {vatAmount > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>VAT (12%):</span>
              <span>{fmt(vatAmount)}</span>
            </div>
          )}
          {ewtAmount > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>EWT (1%):</span>
              <span>− {fmt(ewtAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold bg-gray-100 p-2 rounded">
            <span>Total Amount:</span>
            <span>{fmt(totalAmount)}</span>
          </div>
        </div>
      </div>

      {/* ── SIGNATURES ── */}
      <div className="mt-16 grid grid-cols-3 gap-8 text-[10px]">
        {[
          "Prepared By:",
          "Checked By:",
          "Received By / Customer Conforme:",
        ].map((label) => (
          <div key={label}>
            <div className="border-b border-black mb-1"></div>
            <p className="font-bold">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-[9px] text-gray-400">
        System Generated {documentTitle} ({doNumber}) | Printed on{" "}
        {new Date().toLocaleString()}
      </div>
    </div>
  );
});

export default DeliveryOrderPrint;
